/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import PostureModal from './PostureModal';
import useLockBodyScroll from 'hooks/useLockBodyScroll';
import useOutsideClick from 'hooks/useOutSideClick';
import { postureData } from 'consts/postureData';
import { color } from 'asserts/styles/colors';
import { useAtomValue } from 'jotai';
import companyIdxAtom from 'atom/companyIdxAtom';
import centerInforIdxAtom from 'atom/centerInforIdxAtom';
import { useQuery } from '@tanstack/react-query';
import { QueryKeys, restfetcher } from 'queryClient';
import commonYearAtom from 'atom/commonYear';
import commonMonthAtom from 'atom/commonMonth';

interface ModalProps {
  labels: string[];
  titleText: string;
  detailText: string;
  colors: string[];
  selectedChartType: string | null;
  directions?: string[];
  onClickModal: () => void;
}

const PostureModalLayout = ({
  labels,
  onClickModal,
  selectedChartType,
  titleText: title,
  detailText: detail,
  directions,
  colors
}: ModalProps) => {
  const centerIdx = useAtomValue(centerInforIdxAtom);
  const companyIdx = useAtomValue(companyIdxAtom);
  const selectedYear = useAtomValue(commonYearAtom);
  const selectedMonth = useAtomValue(commonMonthAtom);

  const propsDataTotal: number[] = [];
  const eachPropsData: ModalPropsData[] = [];

  const modalRef = useRef<HTMLDivElement | null>(null);

  const param = {
    companyidx: companyIdx,
    centeridx: centerIdx,
    year: selectedYear,
    month: selectedMonth,
    field: selectedChartType
  };

  const queryKey = [QueryKeys.POSTUREDETAIL, param];

  const getChartDetailData = useQuery({
    queryKey: queryKey,
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'dashboard/posture/stats/detail',
        params: param
      })
  });

  useOutsideClick(modalRef, onClickModal);
  useLockBodyScroll();

  const { data: detailData } = getChartDetailData;

  useEffect(() => {
    getChartDetailData.refetch();
  }, [selectedChartType]);

  let severityArray = [];

  if (detailData) {
    const partDetailData = selectedChartType && detailData[selectedChartType];
    for (const data of postureData) {
      if (data.key === selectedChartType) {
        severityArray = data.severity;
        severityArray.forEach((severity: Severity) => {
          eachPropsData.push(partDetailData[severity.key]);
          propsDataTotal.push(partDetailData[severity.key].total);
        });
      }
    }
  }

  return (
    <Wrapper>
      <ModalWrapper ref={modalRef}>
        <Header>
          <Title>
            {title} ({detail})
          </Title>

          {directions && (
            <SubDirection>
              <p>{directions[0]}</p>
              <p>{directions[1]}</p>
            </SubDirection>
          )}
        </Header>
        <DataWrapper>
          {propsDataTotal.length > 0 && eachPropsData.length > 0 && (
            <PostureModal
              labels={labels}
              propsDataTotal={propsDataTotal}
              eachPropsData={eachPropsData}
              colors={colors}
              selectedChartType={selectedChartType}
            />
          )}
        </DataWrapper>
        <CloseButton onClick={onClickModal}>닫기</CloseButton>
      </ModalWrapper>
    </Wrapper>
  );
};

export default PostureModalLayout;

const Wrapper = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.4);
  font-family: 'Noto Sans KR', sans-serif;
  z-index: 9999;
`;

const Header = styled.div`
  position: relative;
  padding-bottom: 16px;
  text-align: center;
`;

const Title = styled.div`
  line-height: 20px;
  font-weight: 700;
`;

const ModalWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 480px;
  padding: 24px 32px;
  border-radius: 15px;
  background-color: ${color.white};
`;

const DataWrapper = styled.div``;

const CloseButton = styled.button`
  width: 416px;
  height: 36px;
  border-radius: 18px;
  background-color: ${color.mobile};
  color: ${color.white};
`;

const SubDirection = styled.div`
  display: flex;
  justify-content: space-between;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  color: ${color.black06};
  font-size: 13px;
  line-height: 16px;
`;
